import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NbButtonModule, NbCardModule, NbIconModule, NbInputModule, NbSpinnerModule } from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { PreInscricao, PreInscricaoVaga, SituacaoVaga } from '../../core/api/pre-inscricao.dto';
import { PreInscricaoService } from '../../core/api/pre-inscricao.service';
import { CampoDataComponent } from '../../shared/campo-data/campo-data.component';
import { mensagemDeErro } from '../../utils/mensagem-erro';
import { InscricaoComponent } from '../inscricao/inscricao.component';

/**
 * Pré-inscrição pelo link público — `/pre-inscricao/{id}`, sem sessão.
 *
 * A pessoa **não escolhe grupo**: informa a data de nascimento, e a API diz em qual faixa ela
 * cai e se há vaga. A idade é apurada na `dataReferenciaIdade` da campanha, não no dia do
 * envio — por isso a tela mostra essa data por extenso.
 *
 * `GET .../vaga` responde **200 nas quatro situações**, inclusive sem vaga: quem decide é o
 * campo `situacao`, e "sem vaga" não é erro de rede. A consulta **não reserva** — o envio pode
 * voltar `400` se a última vaga for ocupada nesse meio tempo, e aí a tela reconsulta.
 *
 * O formulário é o mesmo da inscrição em turma (`app-inscricao`, em modo campanha).
 */
@Component({
  selector: 'app-pre-inscricao-publica',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
    CampoDataComponent,
    InscricaoComponent,
  ],
  templateUrl: './pre-inscricao-publica.component.html',
  styleUrl: './pre-inscricao-publica.component.scss',
})
export class PreInscricaoPublicaComponent implements OnInit {
  private route = inject(ActivatedRoute);

  campanhaId = '';
  campanha: PreInscricao | null = null;
  carregando = false;
  erroCampanha: string | null = null;

  dataNascimento = '';
  consultando = false;
  vaga: PreInscricaoVaga | null = null;
  erroConsulta: string | null = null;

  /** Só depois de `COM_VAGA` a ficha aparece — antes disso não há o que preencher. */
  preenchendoFicha = false;
  avisoEnvioRecusado: string | null = null;

  /** A consulta do CPF trouxe outra data de nascimento e o grupo foi refeito com ela. */
  avisoGrupoRefeito: string | null = null;

  /**
   * Preenchido, a ficha continua na tela mas não pode ser enviada: a faixa etária da data
   * atual lotou ou não existe. A ficha **não** é escondida — quem já digitou meia página não
   * pode perder o que escreveu por causa de uma reclassificação.
   */
  bloqueioVaga: string | null = null;

  constructor(private service: PreInscricaoService) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.campanhaId = params.get('id') ?? '';
      this.carregarCampanha();
    });
  }

  carregarCampanha(): void {
    if (!this.campanhaId) {
      this.erroCampanha = 'Link de pré-inscrição sem identificador.';
      return;
    }

    this.carregando = true;
    this.erroCampanha = null;
    this.service
      .buscarPublica(this.campanhaId)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (c) => (this.campanha = c),
        error: (err) => {
          this.campanha = null;
          this.erroCampanha = mensagemDeErro(
            err,
            'Não encontramos esta pré-inscrição. Confira o link com a coordenação da catequese.',
          );
        },
      });
  }

  get campanhaEncerrada(): boolean {
    return !!this.campanha && this.campanha.ativa === false;
  }

  get situacao(): SituacaoVaga | null {
    return this.vaga?.situacao ?? null;
  }

  get temVaga(): boolean {
    return this.situacao === 'COM_VAGA';
  }

  /** A porta recebeu uma data completa: guarda e consulta. */
  aoInformarDataNaPorta(iso: string): void {
    this.dataNascimento = iso;
    this.consultarVaga();
  }

  /** Consulta a vaga da data informada. Sem data válida, não chama a API. */
  consultarVaga(): void {
    this.vaga = null;
    this.erroConsulta = null;
    this.preenchendoFicha = false;
    this.avisoEnvioRecusado = null;

    const data = (this.dataNascimento ?? '').trim();
    if (!data || data.length < 10) return;

    this.consultando = true;
    this.service
      .consultarVaga(this.campanhaId, data)
      .pipe(finalize(() => (this.consultando = false)))
      .subscribe({
        next: (v) => (this.vaga = v),
        error: (err) => {
          // 200 cobre as quatro situações, então chegar aqui é falha de verdade.
          this.erroConsulta = mensagemDeErro(err, 'Não foi possível consultar a vaga agora.');
        },
      });
  }

  comecarFicha(): void {
    if (!this.temVaga) return;
    this.avisoGrupoRefeito = null;
    this.bloqueioVaga = null;
    this.preenchendoFicha = true;
  }

  /**
   * O CPF do catequizando devolveu uma data de nascimento diferente da que foi informada na
   * porta. A idade é o que define o grupo, então o grupo é **refeito com a data do documento**,
   * e não com a digitada: se a faixa mudou, ou se a nova faixa não tem vaga, a pessoa precisa
   * saber antes de terminar de preencher.
   */
  aoReceberDataDoCpf(data: string): void {
    if (data === this.dataNascimento && this.bloqueioVaga === null && this.avisoGrupoRefeito) {
      return; // mesma data já reclassificada: não refaz a chamada a cada tecla
    }

    const grupoAntes = this.vaga?.grupo?.nome ?? '';
    this.dataNascimento = data;
    this.avisoGrupoRefeito = null;
    this.avisoEnvioRecusado = null;

    this.consultando = true;
    this.service
      .consultarVaga(this.campanhaId, data)
      .pipe(finalize(() => (this.consultando = false)))
      .subscribe({
        next: (v) => {
          this.vaga = v;

          if (v.situacao !== 'COM_VAGA') {
            // Sem vaga na faixa nova: a ficha sai da frente e a pessoa volta para a porta,
            // com a data nova já preenchida e o motivo à vista. Deixar a ficha aberta sugere
            // que dá para continuar — e não dá.
            this.preenchendoFicha = false;
            this.bloqueioVaga = null;
            this.avisoGrupoRefeito = null;
            return;
          }

          this.bloqueioVaga = null;
          const grupoAgora = v.grupo?.nome ?? '';
          this.avisoGrupoRefeito =
            grupoAgora && grupoAgora !== grupoAntes
              ? `A data de nascimento (${this.dataBr(data)}) reclassificou para o grupo ${grupoAgora}.`
              : `Data de nascimento conferida: ${this.dataBr(data)} · grupo ${grupoAgora || '—'}.`;
        },
        error: (err) => {
          this.bloqueioVaga = mensagemDeErro(
            err,
            'Não foi possível conferir a vaga para esta data de nascimento.',
          );
        },
      });
  }

  /**
   * A API recusou o envio: vaga ocupada no meio do caminho, ou CPF já pré-inscrito nesta
   * campanha. Volta para a porta com o motivo do backend e reconfere o estado da campanha.
   */
  aoRecusarEnvio(mensagem: string): void {
    this.preenchendoFicha = false;
    this.avisoEnvioRecusado = mensagem;
    this.consultarVaga();
    this.carregarCampanha();
  }

  /** `yyyy-MM-dd` → `dd/MM/yyyy`, sem passar por `Date` (data pura não tem fuso). */
  dataBr(iso: string | undefined | null): string {
    const partes = (iso ?? '').split('-');
    return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : '—';
  }
}
